/**
 * useScreenRecording \u2014 Phase 2.1 (per-call mode, 2026-06-22).
 *
 * Lifecycle (cleaner than always-on):
 *
 *   On Ready click:
 *     acquireStream()  \u2192 prompts agent ONCE for getDisplayMedia
 *                          (kept alive for the rest of the shift)
 *
 *   On inbound call accepted / outbound call originated:
 *     startRecording({ callId })
 *                       \u2192 backend /begin
 *                       \u2192 NEW MediaRecorder bound to the existing stream
 *                       \u2192 chunks PUT to Azure
 *                       \u2192 heartbeats every 30s
 *
 *   On call ended (BYE / hangup / cancel / error):
 *     stopRecording()
 *                       \u2192 MediaRecorder.stop() (flush)
 *                       \u2192 commit blocklist (atomically materializes blob)
 *                       \u2192 backend /end (computes sync_offset_ms vs voice)
 *
 *   On Logout click:
 *     releaseStream()
 *                       \u2192 streams[].stop() (browser indicator goes away)
 *                       \u2192 next Ready triggers a fresh permission prompt
 *
 * The MediaStream persists across multiple calls in the same shift, so the
 * agent is prompted exactly ONCE per shift no matter how many calls.
 *
 * Public API:
 *
 *   const sr = useScreenRecording();
 *
 *   sr.state             // 'idle' | 'permissioned' | 'preparing' |
 *                        // 'recording' | 'stopping' | 'denied' | 'error'
 *   sr.error             // last error string or null
 *   sr.recordingId       // current recording row id, or null when not recording
 *   sr.bytesUploaded
 *   sr.chunksUploaded
 *
 *   await sr.acquireStream({ agentExtension })
 *   await sr.startRecording({ callId, agentExtension })
 *   await sr.stopRecording()
 *   sr.releaseStream()
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { screenRecordingsApi } from '../api/client';

const CHUNK_MS = 10_000;        // 10 sec per chunk
const HEARTBEAT_MS = 30_000;    // 30 sec heartbeat cadence
const MAX_RETRIES = 2;
// 2026-07-27 engagement model: after the agent's last call leg ends, keep
// recording for this long so QA sees the agent enter after-call wrap-up.
// A NEW user-initiated call within this window closes the current recording
// immediately and starts a fresh one (one file per call). A continuation leg
// (transfer/conference/merge) within this window cancels the tail and keeps
// the same recording rolling. Hard constant for now; promote to per-tenant
// config later. See SCREEN-RECORDING-ENGAGEMENT-MODEL.md.
const WRAP_TAIL_MS = 10_000;    // 10 sec wrap-up tail
// 2026-07-28 uploader resilience: a single transient chunk PUT failure must
// NOT abort the whole recording. Failed blocks are buffered and retried on
// subsequent ticks; the SAS is re-issued (via the /reissue-sas endpoint) when
// a PUT fails, in case the failure is SAS expiry on a long shift. We only give
// up (abort) after this many DISTINCT blocks are stuck undelivered, which means
// the network is genuinely down rather than a momentary blip.
const MAX_PENDING_BLOCKS = 12;   // ~2 min of 10s chunks buffered before abort
const REISSUE_COOLDOWN_MS = 15_000; // don't hammer /reissue-sas faster than this
// Azure REST API version MUST match (or be older than) the version embedded
// in the SAS token (`sv=...` parameter). Without this header the browser sends
// nothing, Azure uses an account default that can mismatch the SAS, and
// returns 403 "AuthorizationFailure." 2026-02-06 is the version baked into
// the current container SAS \u2014 bump this when the SAS rotates.
const AZURE_REST_API_VERSION = '2026-02-06';

// 2026-08-17 FIX 2: background-tab throttling defense.
// Chrome heavily throttles setInterval/setTimeout (and MediaRecorder timeslice
// flushes) in a backgrounded / occluded tab. That stalled our 30s heartbeat +
// chunk drain, so the server reaper (CX_SR_STALE_HEARTBEAT_SEC) marked live
// recordings `heartbeat_lost` when an agent merely tabbed away to their CRM
// mid-call. Web Workers are NOT throttled the same way, so we drive the
// heartbeat + upload-drain cadence from a tiny inline Worker timer. The actual
// heartbeat POST + block drain still run on the main thread (they need the
// refs + api client + MediaRecorder), but the WORKER is what keeps ticking
// while backgrounded. We also nudge MediaRecorder.requestData() each tick so
// chunks keep flushing even when the timeslice flush is throttled.
const WORKER_TICK_MS = 5_000;    // worker pings the main thread every 5s
function _makeHeartbeatWorker() {
  try {
    const src =
      'let t=null;' +
      'onmessage=function(e){' +
      ' var d=e.data||{};' +
      ' if(d.cmd==="start"){ if(t)clearInterval(t); var ms=d.ms||5000;' +
      '   t=setInterval(function(){postMessage({tick:1});}, ms); }' +
      ' else if(d.cmd==="stop"){ if(t){clearInterval(t);t=null;} }' +
      '};';
    const url = URL.createObjectURL(new Blob([src], { type: 'application/javascript' }));
    const w = new Worker(url);
    // Revoke the object URL once the worker has loaded it (best-effort).
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 2000);
    return w;
  } catch (err) {
    console.warn('[CX-SR] heartbeat worker unavailable, falling back to setInterval', err);
    return null;
  }
}

const PREFERRED_MIME = 'video/webm; codecs=vp9';
const FALLBACK_MIMES = [
  'video/webm; codecs=vp9',
  'video/webm; codecs=vp8',
  'video/webm',
];

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return PREFERRED_MIME;
  }
  for (const m of FALLBACK_MIMES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

// Fixed-length Azure block id: base64("block-NNNNNNNN").
function blockIdForIndex(n) {
  const padded = `block-${String(n).padStart(8, '0')}`;
  return btoa(padded);
}

async function putBlock(sasUrl, blockId, blob, attempt = 0) {
  const url = `${sasUrl}${sasUrl.includes('?') ? '&' : '?'}comp=block&blockid=${encodeURIComponent(blockId)}`;
  try {
    const res = await fetch(url, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'x-ms-version':   AZURE_REST_API_VERSION,
        'Content-Type':   blob.type || 'application/octet-stream',
      },
      body: blob,
    });
    if (!res.ok) {
      let body = '';
      try { body = await res.text(); } catch (_) { /* swallow */ }
      console.warn('[CX-SR] put_block', res.status, 'attempt', attempt,
                   '\n  azure-error:', body.slice(0, 800),
                   '\n  x-ms-request-id:', res.headers.get('x-ms-request-id'),
                   '\n  blob-size:', blob.size,
                   '\n  blob-type:', blob.type);
      throw new Error(`azure_put_block ${res.status}`);
    }
  } catch (err) {
    if (attempt < MAX_RETRIES) {
      await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
      return putBlock(sasUrl, blockId, blob, attempt + 1);
    }
    throw err;
  }
}

async function commitBlockList(sasUrl, blockIds, mimeType) {
  const url = `${sasUrl}${sasUrl.includes('?') ? '&' : '?'}comp=blocklist`;
  const xml =
    `<?xml version="1.0" encoding="utf-8"?>\n<BlockList>\n` +
    blockIds.map((b) => `  <Latest>${b}</Latest>`).join('\n') +
    `\n</BlockList>`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type':            'application/xml',
      'x-ms-version':            AZURE_REST_API_VERSION,
      'x-ms-blob-content-type':  mimeType || 'video/webm',
    },
    body: xml,
  });
  if (!res.ok) {
    let body = '';
    try { body = await res.text(); } catch (_) { /* swallow */ }
    console.warn('[CX-SR] commit_blocklist', res.status, '\n  azure-error:', body.slice(0, 600));
    throw new Error(`azure_commit_blocklist ${res.status}`);
  }
}


/**
 * runAzurePreflight(ext) — real end-to-end connectivity test to Azure Blob.
 * Mints a throwaway SAS via the API, then does the SAME operations a chunk
 * upload does (PUT one small block + commit blocklist) directly to Azure.
 * This exercises DNS + TLS + outbound-to-blob + SAS validity + CORS from the
 * agent's actual network. Added 2026-07-28.
 *
 * Returns { ok: true, bypassAllowed } on success, or
 *         { ok: false, error, stage, bypassAllowed } on failure.
 */
export async function runAzurePreflight(agentExtension) {
  let begin;
  try {
    begin = await screenRecordingsApi.preflightBegin(agentExtension || null);
  } catch (err) {
    // API unreachable / no bucket — can't even mint a SAS. Treat as a
    // control-plane failure (distinct from the Azure data-plane failure we
    // are hunting), but still a blocker for recording.
    return {
      ok: false,
      stage: 'begin',
      error: `preflight_begin_failed: ${err?.message || err}`,
      bypassAllowed: false,
    };
  }
  const sasUrl = begin?.sas_url;
  const bypassAllowed = !!begin?.bypass_allowed;
  if (!sasUrl) {
    return { ok: false, stage: 'begin', error: 'no_sas_url', bypassAllowed };
  }

  // Tiny test payload (a few bytes is enough to prove the write path).
  const testBlob = new Blob(
    [new Uint8Array([0x43, 0x58, 0x2d, 0x53, 0x52, 0x2d, 0x50, 0x46])],
    { type: 'application/octet-stream' },
  );
  const blockId = btoa('preflight-00000000');

  try {
    // Reuse the exact same putBlock the real uploader uses.
    await putBlock(sasUrl, blockId, testBlob);
  } catch (err) {
    return {
      ok: false,
      stage: 'put_block',
      error: String(err?.message || err),
      bypassAllowed,
    };
  }
  try {
    await commitBlockList(sasUrl, [blockId], 'application/octet-stream');
  } catch (err) {
    return {
      ok: false,
      stage: 'commit',
      error: String(err?.message || err),
      bypassAllowed,
    };
  }
  return { ok: true, bypassAllowed };
}


// ---------------------------------------------------------------------
// Multi-monitor (2026-09-10): ExtraScreenRecorder
// ---------------------------------------------------------------------
// The PRIMARY screen (screen_index 0) keeps ALL of the battle-tested
// single-recorder machinery in the hook UNCHANGED (so a one-monitor agent is
// byte-for-byte identical to before). Each ADDITIONAL monitor an agent shares
// (screen_index 1, 2, ...) is driven by one of these lightweight, fully
// self-contained recorder objects that runs alongside the primary: same
// per-call lifecycle (begin -> chunk PUT -> heartbeat -> commit -> end), its
// own SAS/blob/blocklist, reusing the module-level putBlock/commitBlockList.
// A failure on a secondary screen NEVER affects the primary or the call.
class ExtraScreenRecorder {
  constructor(stream, screenIndex) {
    this.stream = stream;
    this.screenIndex = screenIndex;
    this.recorder = null;
    this.recordingId = null;
    this.sasUrl = null;
    this.mime = '';
    this.chunkIndex = 0;
    this.delivered = [];        // block ids that landed in Azure (commit these)
    this.pending = [];          // [{blockId, blob}] awaiting PUT
    this.totalBytes = 0;
    this.stopped = false;
    this.hbWorker = null;
    this.hbFallback = null;
    this.lastHbSent = 0;
    this.lastReissueAt = 0;
    this.busy = false;
  }

  async _drain() {
    if (this.busy) return;
    this.busy = true;
    try {
      while (this.pending.length > 0) {
        const { blockId, blob } = this.pending[0];
        try {
          await putBlock(this.sasUrl, blockId, blob);
          this.pending.shift();
          this.delivered.push(blockId);
          this.totalBytes += blob.size;
        } catch (err) {
          // Best-effort SAS reissue, then leave buffered for next tick.
          const now = Date.now();
          if (now - this.lastReissueAt >= REISSUE_COOLDOWN_MS && this.recordingId) {
            this.lastReissueAt = now;
            try {
              const res = await screenRecordingsApi.reissueSas(this.recordingId);
              if (res && res.sas_url) this.sasUrl = res.sas_url;
            } catch (_) { /* swallow */ }
          }
          break;
        }
      }
    } finally {
      this.busy = false;
    }
    // Sustained failure on a SECONDARY screen: abort just this screen, quietly.
    if (this.pending.length >= MAX_PENDING_BLOCKS) {
      try {
        if (this.recordingId) {
          await screenRecordingsApi.abort(this.recordingId, {
            failure_reason: `extra_screen_${this.screenIndex}_upload_failed`,
          });
        }
      } catch (_) { /* swallow */ }
      this._teardownTimers();
      this.recorder = null;
      this.recordingId = null;
    }
  }

  _teardownTimers() {
    if (this.hbWorker) {
      try { this.hbWorker.postMessage({ cmd: 'stop' }); } catch (_) {}
      try { this.hbWorker.onmessage = null; this.hbWorker.terminate(); } catch (_) {}
      this.hbWorker = null;
    }
    if (this.hbFallback) { clearInterval(this.hbFallback); this.hbFallback = null; }
  }

  async start({ callId = null, agentExtension = null } = {}) {
    if (!this.stream || !this.stream.getTracks().some((t) => t.readyState === 'live')) {
      return false;   // this monitor's stream is gone; skip silently
    }
    this.stopped = false;
    this.chunkIndex = 0;
    this.delivered = [];
    this.pending = [];
    this.totalBytes = 0;
    this.mime = pickMimeType();
    let begin;
    try {
      begin = await screenRecordingsApi.begin({
        call_id: callId,
        agent_extension: agentExtension,
        started_at_utc: new Date().toISOString(),
        mode: 'per_call',
        video_bps: 250_000,
        codec: 'vp9',
        screen_index: this.screenIndex,
      });
    } catch (err) {
      console.warn('[CX-SR] extra screen /begin failed', this.screenIndex, err);
      return false;
    }
    this.recordingId = begin.recording_id;
    this.sasUrl = begin.sas_url;
    let recorder;
    try {
      recorder = new MediaRecorder(this.stream, {
        mimeType: this.mime || undefined,
        videoBitsPerSecond: 250_000,
      });
    } catch (err) {
      console.warn('[CX-SR] extra screen MediaRecorder failed', this.screenIndex, err);
      try { await screenRecordingsApi.abort(begin.recording_id, { failure_reason: 'mediarecorder_init' }); } catch (_) {}
      this.recordingId = null;
      return false;
    }
    this.recorder = recorder;
    recorder.ondataavailable = async (e) => {
      const blob = e?.data;
      if (!blob || blob.size === 0) return;
      const idx = this.chunkIndex++;
      const blockId = blockIdForIndex(idx);
      this.pending.push({ blockId, blob });
      await this._drain();
    };
    recorder.onerror = (ev) => console.warn('[CX-SR] extra recorder error', this.screenIndex, ev);
    recorder.start(CHUNK_MS);

    const tick = () => {
      if (!this.recordingId) return;
      try { if (this.recorder && this.recorder.state === 'recording') this.recorder.requestData(); } catch (_) {}
      if (this.pending.length > 0) this._drain().catch(() => {});
      const now = Date.now();
      if (now - this.lastHbSent >= HEARTBEAT_MS) {
        this.lastHbSent = now;
        screenRecordingsApi.heartbeat(this.recordingId, {
          chunks_uploaded: this.delivered.length,
          bytes_uploaded: this.totalBytes,
        }).catch(() => {});
      }
    };
    this.lastHbSent = Date.now();
    const w = _makeHeartbeatWorker();
    if (w) {
      this.hbWorker = w;
      w.onmessage = (ev) => { if (ev && ev.data && ev.data.tick) tick(); };
      w.postMessage({ cmd: 'start', ms: WORKER_TICK_MS });
    } else {
      this.hbFallback = setInterval(tick, WORKER_TICK_MS);
    }
    return true;
  }

  async stop({ callEndedAtMs = null } = {}) {
    if (!this.recordingId || this.stopped) return;
    this.stopped = true;
    this._teardownTimers();
    // Flush recorder.
    await new Promise((resolve) => {
      const r = this.recorder;
      if (!r || r.state === 'inactive') return resolve();
      const h = () => { r.removeEventListener('stop', h); resolve(); };
      r.addEventListener('stop', h);
      try { r.requestData(); } catch (_) {}
      try { r.stop(); } catch (_) { resolve(); }
    });
    await new Promise((r) => setTimeout(r, 600));
    for (let i = 0; i < 3 && this.pending.length > 0; i++) {
      await this._drain();
      if (this.pending.length === 0) break;
      await new Promise((r) => setTimeout(r, 800 * (i + 1)));
    }
    if (this.sasUrl && this.delivered.length > 0) {
      try { await commitBlockList(this.sasUrl, this.delivered, this.mime); }
      catch (err) { console.warn('[CX-SR] extra commit failed', this.screenIndex, err); }
    }
    try {
      await screenRecordingsApi.end(this.recordingId, {
        ended_at_utc: new Date().toISOString(),
        total_bytes: this.totalBytes,
        total_chunks: this.delivered.length,
        call_ended_at_utc: callEndedAtMs ? new Date(callEndedAtMs).toISOString() : null,
      });
    } catch (err) { console.warn('[CX-SR] extra /end failed', this.screenIndex, err); }
    this.recorder = null;
    this.recordingId = null;
  }

  releaseStream() {
    try { this.stream && this.stream.getTracks().forEach((t) => t.stop()); } catch (_) {}
    this.stream = null;
  }
}

export function useScreenRecording() {
  const [state, setState]                   = useState('idle');
  const [error, setError]                   = useState(null);
  const [recordingId, setRecordingId]       = useState(null);
  const [bytesUploaded, setBytesUploaded]   = useState(0);
  const [chunksUploaded, setChunksUploaded] = useState(0);

  // Persistent across calls (shift-lived):
  const streamRef        = useRef(null);
  // Multi-monitor (2026-09-10): additional monitor streams the agent shared
  // (screen_index >= 1). Shift-lived like the primary. Each becomes an
  // ExtraScreenRecorder per call. Empty for single-monitor agents.
  const extraStreamsRef  = useRef([]);          // MediaStream[]
  const extraRecordersRef = useRef([]);         // ExtraScreenRecorder[] (per active call)
  const maxScreensRef    = useRef(1);

  // Per-recording (reset each call):
  const recorderRef      = useRef(null);
  const sasUrlRef        = useRef(null);
  const blobPathRef      = useRef(null);
  const blockIdsRef      = useRef([]);
  const recordingIdRef   = useRef(null);
  const chunkIndexRef    = useRef(0);
  const heartbeatTimer   = useRef(null);
  // 2026-08-17 FIX 2: inline Web Worker that drives the heartbeat/drain cadence
  // so a backgrounded tab (throttled setInterval) doesn't stall heartbeats and
  // get the live recording reaped as `heartbeat_lost`. `heartbeatFallback` is a
  // main-thread setInterval used only if the Worker couldn't be created.
  const hbWorkerRef      = useRef(null);
  const heartbeatFallback = useRef(null);
  const lastHbSentRef    = useRef(0);
  const stoppedRef       = useRef(false);
  // 2026-07-15 pm: hold a ref to stopRecording so startRecording can
  // call it (declaration order otherwise makes it undefined).
  const stopRecordingRef = useRef(null);
  const totalBytesRef    = useRef(0);
  const mimeRef          = useRef('');
  // 2026-07-28 uploader resilience:
  //   pendingBlocksRef: [{ blockId, blob }] blocks that failed to PUT and are
  //     awaiting retry. Drained on each tick + at stop (before commit).
  //   lastReissueAtRef: throttle for /reissue-sas.
  //   uploaderBusyRef: guards against overlapping drain runs.
  const pendingBlocksRef = useRef([]);
  const lastReissueAtRef = useRef(0);
  const uploaderBusyRef  = useRef(false);
  // Block ids that ACTUALLY landed in Azure (in order). We commit ONLY these,
  // not every received block id — committing a never-PUT block id makes Azure
  // reject the whole blocklist. 2026-07-28.
  const deliveredBlockIdsRef = useRef([]);

  // For lifecycle hygiene:
  const stateRef         = useRef('idle');
  stateRef.current = state;

  // -----------------------------------------------------------------
  // Engagement coordinator state (2026-07-27)
  // -----------------------------------------------------------------
  // engagementLegs: set of live leg ids the agent is party to. Recording
  // spans the whole engagement (first leg -> last leg + wrap tail), NOT one
  // SIP session. See SCREEN-RECORDING-ENGAGEMENT-MODEL.md.
  const engagementLegsRef   = useRef(new Set());
  const engagementActiveRef = useRef(false);
  const wrapTailTimerRef    = useRef(null);
  const legSeqRef           = useRef(0);
  // Snapshot of the callId/agentExtension for the CURRENT engagement, so a
  // continuation leg doesn't need to re-supply them.
  const engagementMetaRef   = useRef({ callId: null, agentExtension: null });
  // Refs to the start/stop fns so the coordinator (declared later) is
  // reachable from the imperative window handle without ordering issues.
  const startRecordingRef   = useRef(null);

  const _clearWrapTail = useCallback(() => {
    if (wrapTailTimerRef.current) {
      clearInterval(wrapTailTimerRef.current);
      wrapTailTimerRef.current = null;
    }
  }, []);

  const _resetPerRecording = useCallback(() => {
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = null;
    }
    // 2026-08-17 FIX 2: tear down the heartbeat worker + fallback.
    if (hbWorkerRef.current) {
      try { hbWorkerRef.current.postMessage({ cmd: 'stop' }); } catch (_) {}
      try { hbWorkerRef.current.onmessage = null; hbWorkerRef.current.terminate(); } catch (_) {}
      hbWorkerRef.current = null;
    }
    if (heartbeatFallback.current) {
      clearInterval(heartbeatFallback.current);
      heartbeatFallback.current = null;
    }
    lastHbSentRef.current = 0;
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch (_) { /* swallow */ }
    }
    recorderRef.current = null;
    sasUrlRef.current = null;
    blobPathRef.current = null;
    blockIdsRef.current = [];
    recordingIdRef.current = null;
    chunkIndexRef.current = 0;
    pendingBlocksRef.current = [];
    lastReissueAtRef.current = 0;
    uploaderBusyRef.current = false;
    deliveredBlockIdsRef.current = [];
    totalBytesRef.current = 0;
    stoppedRef.current = false;
    mimeRef.current = '';
    setRecordingId(null);
    setBytesUploaded(0);
    setChunksUploaded(0);
  }, []);

  // -----------------------------------------------------------------
  // 2026-07-28 uploader resilience helpers
  // -----------------------------------------------------------------

  // Re-issue the blob SAS (same blob_path) when a PUT fails - handles SAS
  // expiry on long shifts. Throttled so we don't hammer the endpoint.
  const _tryReissueSas = useCallback(async () => {
    const now = Date.now();
    if (now - lastReissueAtRef.current < REISSUE_COOLDOWN_MS) return false;
    lastReissueAtRef.current = now;
    const recId = recordingIdRef.current;
    if (!recId) return false;
    try {
      const res = await screenRecordingsApi.reissueSas(recId);
      if (res && res.sas_url) {
        sasUrlRef.current = res.sas_url;
        console.warn('[CX-SR] re-issued SAS after chunk failure');
        return true;
      }
    } catch (err) {
      console.warn('[CX-SR] reissue-sas failed', err);
    }
    return false;
  }, []);

  // Drain the pending-blocks queue in order. On persistent failure, keep the
  // blocks buffered (retried next tick / at stop). Only when the queue
  // overflows MAX_PENDING_BLOCKS do we conclude the network is genuinely down
  // and abort. Returns true if the queue fully drained.
  const _drainPendingBlocks = useCallback(async () => {
    if (uploaderBusyRef.current) return false;
    uploaderBusyRef.current = true;
    try {
      while (pendingBlocksRef.current.length > 0) {
        const { blockId, blob } = pendingBlocksRef.current[0];
        try {
          await putBlock(sasUrlRef.current, blockId, blob);
          pendingBlocksRef.current.shift();  // delivered; drop from queue
          deliveredBlockIdsRef.current.push(blockId);  // commit only landed blocks
          totalBytesRef.current += blob.size;
          setBytesUploaded(totalBytesRef.current);
          setChunksUploaded(
            (chunkIndexRef.current || 0) - pendingBlocksRef.current.length,
          );
        } catch (err) {
          // This block failed. Try a fresh SAS once (expiry is a common
          // long-shift cause), then leave it buffered for the next tick.
          console.warn(
            '[CX-SR] chunk PUT failed; buffered for retry',
            'pending=', pendingBlocksRef.current.length, err,
          );
          await _tryReissueSas();
          break;  // stop draining; retry on next tick
        }
      }
    } finally {
      uploaderBusyRef.current = false;
    }

    // Overflow => sustained failure => abort loudly (compliance-visible).
    if (pendingBlocksRef.current.length >= MAX_PENDING_BLOCKS) {
      const lastErr = 'chunk_upload_failed: sustained (buffer overflow)';
      console.error('[CX-SR] uploader giving up after sustained failures; aborting');
      setError(lastErr);
      try {
        if (recordingIdRef.current) {
          await screenRecordingsApi.abort(recordingIdRef.current, {
            failure_reason: lastErr,
          });
        }
      } catch (_) { /* swallow */ }
      _resetPerRecording();
      setState('permissioned');
      return false;
    }
    return pendingBlocksRef.current.length === 0;
  }, [_tryReissueSas, _resetPerRecording]);

  // -----------------------------------------------------------------
  // Step 1: acquireStream() \u2014 ask agent for screen permission ONCE per shift
  // -----------------------------------------------------------------

  const acquireStream = useCallback(async ({ agentExtension = null } = {}) => {
    // Already have a live stream? No-op.
    if (streamRef.current && streamRef.current.getTracks().some(t => t.readyState === 'live')) {
      console.log('[CX-SR] acquireStream: already have live stream');
      return true;
    }
    setError(null);
    setState('preparing');

    // Backend gate first \u2014 if agent toggle is off, skip the permission prompt
    // entirely (don't be annoying).
    try {
      const sr = await screenRecordingsApi.shouldRecord({
        agent_extension: agentExtension,
      });
      if (!sr?.enabled) {
        console.log('[CX-SR] acquireStream: backend says screen recording disabled');
        setState('idle');
        return false;
      }
      // Multi-monitor (2026-09-10): how many screens this agent may record.
      maxScreensRef.current = Math.max(1, Number(sr?.max_screens || 1));
    } catch (err) {
      console.warn('[CX-SR] acquireStream: should-record failed, treating as off', err);
      setState('idle');
      return false;
    }

    // Browser prompt.
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'monitor',
          frameRate: { ideal: 5, max: 10 },
          width:     { ideal: 1280, max: 1920 },
          cursor:    'always',
        },
        audio: false,
        selfBrowserSurface: 'exclude',
        surfaceSwitching:   'include',
        systemAudio:        'exclude',
      });
    } catch (err) {
      console.warn('[CX-SR] acquireStream: permission denied / cancelled', err);
      setError('permission_denied');
      setState('denied');
      return false;
    }
    streamRef.current = stream;

    // If agent ends the browser share via the browser-native indicator, we
    // treat that as "release stream" \u2014 no more recordings until next Ready.
    const vt = stream.getVideoTracks?.()[0];
    if (vt) {
      vt.addEventListener('ended', () => {
        if (streamRef.current === stream) {
          console.log('[CX-SR] browser-native screen share ended; releasing stream');
          releaseStream();
        }
      });
    }

    // Multi-monitor (2026-09-10): DO NOT auto-prompt for extra monitors here.
    // A surprise second browser prompt is bad UX (Fernando 2026-09-10). Instead
    // the ScreenShareGate shows an explicit, opt-in "Add second screen" button
    // that calls acquireExtraScreen() below — one intentional flow, and
    // single-monitor agents never see a second prompt at all.
    extraStreamsRef.current = [];

    setState('permissioned');
    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------------------------------------
  // Multi-monitor (2026-09-10): acquireExtraScreen() — opt-in 2nd+ monitor
  // -----------------------------------------------------------------
  // Called by the gate's "Add second screen" button (never auto-fired). Prompts
  // for ONE more monitor and adds it to the extras list. Best-effort: cancel =
  // no-op; never blocks the call. Returns true if a monitor was added.
  const acquireExtraScreen = useCallback(async () => {
    const maxScreens = Math.max(1, maxScreensRef.current || 1);
    if ((extraStreamsRef.current?.length || 0) >= maxScreens - 1) {
      console.log('[CX-SR] acquireExtraScreen: already at max extra screens');
      return false;
    }
    let extra;
    try {
      extra = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'monitor',
          frameRate: { ideal: 5, max: 10 },
          width:     { ideal: 1280, max: 1920 },
          cursor:    'always',
        },
        audio: false,
        selfBrowserSurface: 'exclude',
        surfaceSwitching:   'include',
        systemAudio:        'exclude',
      });
    } catch (err) {
      console.log('[CX-SR] acquireExtraScreen: not shared (ok)', err?.name);
      return false;
    }
    extraStreamsRef.current = [...(extraStreamsRef.current || []), extra];
    const evt = extra.getVideoTracks?.()[0];
    if (evt) {
      evt.addEventListener('ended', () => {
        extraStreamsRef.current = extraStreamsRef.current.filter((s) => s !== extra);
      });
    }
    // If a call is ALREADY in progress, start recording this new screen now so
    // it joins the current call mid-stream (best-effort).
    try {
      if (engagementActiveRef.current) {
        const idx = extraStreamsRef.current.length;   // 1-based screen_index
        const rec = new ExtraScreenRecorder(extra, idx);
        extraRecordersRef.current = [...(extraRecordersRef.current || []), rec];
        rec.start({
          callId: engagementMetaRef.current?.callId || null,
          agentExtension: engagementMetaRef.current?.agentExtension || null,
        }).catch(() => {});
      }
    } catch (_) { /* swallow */ }
    console.log('[CX-SR] acquireExtraScreen: captured extra screen');
    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------------------------------------
  // Step 2: startRecording({ callId }) \u2014 begin recording one call
  // -----------------------------------------------------------------

  // 2026-07-16 hardening: refs for the fallback rebind poller.
  const fallbackPollTimerRef = useRef(null);
  const fallbackPollAbortedRef = useRef(false);

  const _cancelFallbackPoll = useCallback(() => {
    if (fallbackPollTimerRef.current) {
      clearTimeout(fallbackPollTimerRef.current);
      fallbackPollTimerRef.current = null;
    }
    fallbackPollAbortedRef.current = true;
  }, []);

  const startRecording = useCallback(async ({
    callId = null,
    agentExtension = null,
  } = {}) => {
    if (!streamRef.current) {
      console.log('[CX-SR] startRecording: no stream \u2014 attempting to acquire');
      const ok = await acquireStream({ agentExtension });
      if (!ok) return false;
    }
    // 2026-07-15 pm fix: if a previous recording is still running (e.g.
    // the agent hit +NewCall for a consult while an inbound recording
    // was in flight, OR they just placed a new outbound on top of an
    // inbound), gracefully STOP the previous one and start a fresh
    // recording for the new call. Previously we silently returned false
    // and the new call had no screen coverage.
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      console.log('[CX-SR] startRecording: previous recording still active - stopping before starting new');
      try {
        if (stopRecordingRef.current) {
          await stopRecordingRef.current();
        }
      } catch (err) {
        console.warn('[CX-SR] stopRecording during pre-empt failed', err);
      }
    }
    _resetPerRecording();
    setError(null);
    setState('preparing');

    const mime = pickMimeType();
    mimeRef.current = mime;
    const startedAtUtc = new Date().toISOString();
    let begin;
    try {
      begin = await screenRecordingsApi.begin({
        call_id: callId,
        agent_extension: agentExtension,
        started_at_utc: startedAtUtc,
        mode: 'per_call',
        video_bps: 250_000,
        codec: 'vp9',
      });
    } catch (err) {
      console.error('[CX-SR] /begin failed', err);
      setError(err?.message || 'begin_failed');
      setState('error');
      return false;
    }
    recordingIdRef.current = begin.recording_id;
    setRecordingId(begin.recording_id);
    sasUrlRef.current = begin.sas_url;
    blobPathRef.current = begin.blob_path;

    // Build the recorder bound to the SHARED stream.
    let recorder;
    try {
      recorder = new MediaRecorder(streamRef.current, {
        mimeType: mime || undefined,
        videoBitsPerSecond: 250_000,
      });
    } catch (err) {
      console.error('[CX-SR] MediaRecorder construction failed', err);
      setError(err?.message || 'media_recorder_failed');
      try { await screenRecordingsApi.abort(begin.recording_id, { failure_reason: 'mediarecorder_init' }); } catch (_) {}
      _resetPerRecording();
      setState('permissioned');
      return false;
    }
    recorderRef.current = recorder;
    recorder.ondataavailable = async (e) => {
      const blob = e?.data;
      if (!blob || blob.size === 0) return;
      const idx = chunkIndexRef.current++;
      const blockId = blockIdForIndex(idx);
      blockIdsRef.current.push(blockId);
      // 2026-07-28: queue the block, then drain the queue. A transient PUT
      // failure keeps the block buffered for retry instead of aborting the
      // whole recording. Only a SUSTAINED failure (queue overflow) aborts.
      pendingBlocksRef.current.push({ blockId, blob });
      await _drainPendingBlocks();
    };
    recorder.onerror = (ev) => {
      console.warn('[CX-SR] recorder error', ev);
      setError('recorder_error');
    };
    recorder.start(CHUNK_MS);
    setState('recording');

    // Multi-monitor (2026-09-10): start a parallel recorder for each ADDITIONAL
    // screen the agent shared. Best-effort + fully isolated: a failure on a
    // secondary screen never touches the primary or the call. Fire-and-forget
    // the starts so they don't delay the primary path.
    try {
      extraRecordersRef.current = [];
      const extras = (extraStreamsRef.current || []).filter(
        (s) => s && s.getTracks().some((t) => t.readyState === 'live'),
      );
      extras.forEach((s, i) => {
        const rec = new ExtraScreenRecorder(s, i + 1);   // screen_index 1..N
        extraRecordersRef.current.push(rec);
        rec.start({ callId, agentExtension }).catch((err) =>
          console.warn('[CX-SR] extra screen start failed', i + 1, err),
        );
      });
    } catch (err) {
      console.warn('[CX-SR] extra screens start skipped', err);
    }

    // ---- Heartbeat + drain tick (2026-08-17 FIX 2: worker-driven) ----
    // One tick handler; the Worker (or fallback interval) calls it every
    // WORKER_TICK_MS. We send a heartbeat POST no more often than HEARTBEAT_MS,
    // but we nudge requestData() + drain the pending queue EVERY tick so chunks
    // keep flushing even when the MediaRecorder timeslice flush is throttled in
    // a backgrounded tab. This is what keeps `last_heartbeat_at` fresh so the
    // server reaper doesn't kill a live-but-backgrounded recording.
    const _tick = () => {
      if (!recordingIdRef.current) return;
      // Nudge the recorder to emit a chunk NOW (defeats timeslice throttling).
      try {
        const r = recorderRef.current;
        if (r && r.state === 'recording') r.requestData();
      } catch (_) { /* swallow */ }
      // Drain any buffered blocks (retries + newly-flushed chunks).
      if (pendingBlocksRef.current.length > 0) {
        _drainPendingBlocks().catch(() => {});
      }
      // Rate-limit the actual heartbeat POST to the 30s cadence.
      const now = Date.now();
      if (now - lastHbSentRef.current >= HEARTBEAT_MS) {
        lastHbSentRef.current = now;
        screenRecordingsApi.heartbeat(recordingIdRef.current, {
          chunks_uploaded: deliveredBlockIdsRef.current.length,
          bytes_uploaded:  totalBytesRef.current,
        }).catch((err) => console.warn('[CX-SR] heartbeat failed', err));
      }
    };
    lastHbSentRef.current = Date.now();
    const w = _makeHeartbeatWorker();
    if (w) {
      hbWorkerRef.current = w;
      w.onmessage = (ev) => { if (ev && ev.data && ev.data.tick) _tick(); };
      w.postMessage({ cmd: 'start', ms: WORKER_TICK_MS });
    } else {
      // Fallback: main-thread interval (subject to throttling, but better than
      // nothing on a browser without Worker support).
      heartbeatFallback.current = setInterval(_tick, WORKER_TICK_MS);
    }

    // -------- 2026-07-16 hardening: fallback rebind poller ---------
    // If the callId we started with doesn't look like an Asterisk
    // uniqueid (numeric.numeric), the SSE rebind path is our only way
    // to link this screen recording to its voice recording. That path
    // failed silently for 5.5 hours on 2026-07-16 (AMI handlers stopped
    // emitting SSE without raising). This poller is the belt & suspenders:
    // 5s, 15s, 30s, 60s in, ask the backend for the agent's current
    // Asterisk uniqueid from agent_live_state and self-rebind if it looks
    // right. Bounded (4 attempts) so no runaway timers.
    fallbackPollAbortedRef.current = false;
    const looksLikeUniqueid = (v) => typeof v === 'string' && /^\d+\.\d+$/.test(v);
    if (!looksLikeUniqueid(callId) && agentExtension) {
      const startedAtIso = startedAtUtc;
      // 2026-07-27: start IMMEDIATELY and poll densely early. Short calls
      // (e.g. a 3s test call) end before the old first attempt at 5s, so the
      // agent_live_state on_call window closes and we never captured the
      // uniqueid -> call_id stayed NULL. Absolute-from-start schedule:
      // 0s, 1s, 2s, 3.5s, 5s, 8s, 12s, 20s, 35s, 60s.
      const scheduleMs = [0, 1000, 2000, 3500, 5000, 8000, 12000, 20000, 35000, 60000];
      const recIdSnapshot = begin.recording_id;

      const runAttempt = async (attemptIdx) => {
        if (fallbackPollAbortedRef.current) return;
        if (recordingIdRef.current !== recIdSnapshot) return; // recording changed/ended
        try {
          const res = await screenRecordingsApi.lookupUniqueid(
            agentExtension, startedAtIso,
          );
          if (res && res.uniqueid && looksLikeUniqueid(res.uniqueid)) {
            console.log(
              '[CX-SR] fallback poll: got uniqueid',
              res.uniqueid, 'attempt', attemptIdx + 1,
            );
            try {
              const rebindOut = await screenRecordingsApi.rebindCallId(
                recIdSnapshot, res.uniqueid,
              );
              console.log('[CX-SR] fallback poll: rebind result', rebindOut);
              return; // done, no more attempts
            } catch (err) {
              console.warn('[CX-SR] fallback poll: rebind failed', err);
            }
          }
        } catch (err) {
          console.warn('[CX-SR] fallback poll: lookup failed', err);
        }
        // Schedule next attempt if any remain.
        const nextIdx = attemptIdx + 1;
        if (nextIdx < scheduleMs.length && !fallbackPollAbortedRef.current) {
          fallbackPollTimerRef.current = setTimeout(
            () => runAttempt(nextIdx),
            scheduleMs[nextIdx] - scheduleMs[attemptIdx],
          );
        }
      };

      // Kick off the chain at the first tick.
      fallbackPollTimerRef.current = setTimeout(
        () => runAttempt(0), scheduleMs[0],
      );
    }

    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acquireStream, _resetPerRecording]);

  // -----------------------------------------------------------------
  // Step 3: stopRecording() \u2014 end one recording cleanly
  // -----------------------------------------------------------------

  const stopRecording = useCallback(async (opts = {}) => {
    const { callEndedAtMs = null } = opts;
    // Publish ourselves via ref so startRecording (declared earlier) can
    // pre-empt a previous recording when a new call begins.
    // (assignment happens below after the fn is defined; this comment
    // is here for future readers.)
    if (!recordingIdRef.current) {
      // No active recording \u2014 nothing to do.
      return;
    }
    if (stoppedRef.current) return;
    stoppedRef.current = true;
    // 2026-07-16 hardening: cancel any pending fallback rebind poll
    // before we start tearing down. Nothing to rebind to after end.
    _cancelFallbackPoll();
    setState('stopping');

    // Flush the MediaRecorder.
    const finalFlush = new Promise((resolve) => {
      const r = recorderRef.current;
      if (!r || r.state === 'inactive') return resolve();
      const stopHandler = () => { r.removeEventListener('stop', stopHandler); resolve(); };
      r.addEventListener('stop', stopHandler);
      try { r.requestData(); } catch (_) { /* swallow */ }
      try { r.stop(); } catch (_) { resolve(); }
    });
    await finalFlush;

    // Beat for the last chunk PUT to drain.
    await new Promise((r) => setTimeout(r, 600));

    // 2026-07-28: final drain of any buffered blocks before commit. Retry a
    // few times with a fresh SAS in between so a late blip doesn't lose the
    // tail of the recording. Whatever still won't deliver is dropped from the
    // commit (we commit ONLY blocks that actually landed).
    for (let i = 0; i < 3 && pendingBlocksRef.current.length > 0; i++) {
      const drained = await _drainPendingBlocks();
      if (drained) break;
      await _tryReissueSas();
      await new Promise((r) => setTimeout(r, 800 * (i + 1)));
    }
    if (pendingBlocksRef.current.length > 0) {
      console.warn(
        '[CX-SR] committing with', pendingBlocksRef.current.length,
        'undelivered block(s) dropped',
      );
    }

    // Commit blob — ONLY the blocks that actually landed in Azure.
    if (sasUrlRef.current && deliveredBlockIdsRef.current.length > 0) {
      try {
        await commitBlockList(
          sasUrlRef.current, deliveredBlockIdsRef.current, mimeRef.current,
        );
      } catch (err) {
        console.warn('[CX-SR] commit blocklist failed', err);
        setError(err?.message || 'commit_failed');
      }
    }

    // Close backend row.
    if (recordingIdRef.current) {
      try {
        await screenRecordingsApi.end(recordingIdRef.current, {
          ended_at_utc: new Date().toISOString(),
          total_bytes: totalBytesRef.current,
          total_chunks: deliveredBlockIdsRef.current.length,
          // 2026-07-27: true call-end wall-clock (when the agent's last leg
          // ended, BEFORE the ~10s wrap tail). Lets the backend compute the
          // real call window for sync-offset + report the tail separately,
          // so the wrap tail doesn't poison voice/screen alignment.
          call_ended_at_utc: callEndedAtMs ? new Date(callEndedAtMs).toISOString() : null,
        });
      } catch (err) {
        console.warn('[CX-SR] /end failed', err);
        setError(err?.message || 'end_failed');
      }
    }

    // Multi-monitor (2026-09-10): stop every secondary-screen recorder in
    // parallel (each commits + /ends its own blob). Best-effort; failures here
    // never affect the primary's clean teardown below.
    try {
      const extras = extraRecordersRef.current || [];
      extraRecordersRef.current = [];
      await Promise.all(
        extras.map((rec) => rec.stop({ callEndedAtMs }).catch((err) =>
          console.warn('[CX-SR] extra screen stop failed', rec?.screenIndex, err),
        )),
      );
    } catch (err) {
      console.warn('[CX-SR] extra screens stop skipped', err);
    }

    _resetPerRecording();
    // Stream stays alive; we go back to 'permissioned' ready for the next call.
    setState(streamRef.current ? 'permissioned' : 'idle');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_resetPerRecording]);

  // Publish stopRecording via ref so startRecording (declared earlier)
  // can pre-empt a running recording when a new call begins.
  stopRecordingRef.current = stopRecording;
  startRecordingRef.current = startRecording;

  // -----------------------------------------------------------------
  // Engagement coordinator (2026-07-27)
  //
  // The recorder lifecycle is driven by ENGAGEMENT edges, not per-SIP
  // session. useSIP.js reports:
  //   legLive(session, { origin, callId, agentExtension })
  //   legGone(session)
  //   logout()
  //
  // origin: 'user'          -> a fresh agent-initiated call (inbound answer,
  //                            parked-incoming accept, outbound dial)
  //         'continuation'   -> a leg that belongs to the ongoing interaction
  //                            (consult / conference conf-join / merge swap)
  //
  // See SCREEN-RECORDING-ENGAGEMENT-MODEL.md for the full spec.
  // -----------------------------------------------------------------

  const _legIdFor = useCallback((session) => {
    if (!session) return `leg-anon-${++legSeqRef.current}`;
    if (!session.__cxLegId) {
      session.__cxLegId = `leg-${++legSeqRef.current}`;
    }
    return session.__cxLegId;
  }, []);

  // releaseStreamRef: set below once releaseStream is defined, so the wrap
  // tail can drop the screen-share stream after a logout-initiated tail.
  const releaseStreamRef = useRef(null);
  const wrapTailReleaseAfterRef = useRef(false);
  // Wall-clock time (ms epoch) the agent's LAST leg ended. Used both to fire
  // the wrap tail on real elapsed time (robust vs background-tab setTimeout
  // throttling) and to tell the backend the true call-end so the ~10s tail
  // isn't counted as call/sync time.
  const callEndedAtMsRef = useRef(null);

  const _fireWrapStop = useCallback(async () => {
    _clearWrapTail();
    if (engagementLegsRef.current.size !== 0) {
      // A leg came back; do not stop.
      return;
    }
    console.log('[CX-SR] engagement: wrap tail elapsed -> stopping recording');
    engagementActiveRef.current = false;
    engagementMetaRef.current = { callId: null, agentExtension: null };
    const callEndedAtMs = callEndedAtMsRef.current;
    callEndedAtMsRef.current = null;
    try {
      if (stopRecordingRef.current) {
        await stopRecordingRef.current({ callEndedAtMs });
      }
    } catch (err) {
      console.warn('[CX-SR] engagement: stop after wrap tail failed', err);
    }
    if (wrapTailReleaseAfterRef.current) {
      wrapTailReleaseAfterRef.current = false;
      try { if (releaseStreamRef.current) releaseStreamRef.current(); } catch (_) {}
    }
  }, [_clearWrapTail]);

  // Wall-clock-based wrap tail. Instead of a single 10s setTimeout (which a
  // backgrounded/throttled tab can stretch to 30-60s+), we record the
  // call-end wall-clock time and poll every 1s, firing the stop once real
  // elapsed >= WRAP_TAIL_MS. Intervals self-correct after throttling, so the
  // recording stops within ~1s of the true 10s mark even if the tab was
  // briefly backgrounded. (Root cause of the 2026-07-27 48s-tail bug.)
  const _armWrapTail = useCallback((releaseStreamAfter = false) => {
    _clearWrapTail();
    wrapTailReleaseAfterRef.current = releaseStreamAfter;
    callEndedAtMsRef.current = Date.now();
    console.log('[CX-SR] engagement: last leg gone -> arming', WRAP_TAIL_MS,
                'ms wrap tail (releaseAfter=', releaseStreamAfter, ')');
    wrapTailTimerRef.current = setInterval(() => {
      const startedTailAt = callEndedAtMsRef.current;
      if (startedTailAt == null) { _clearWrapTail(); return; }
      const elapsed = Date.now() - startedTailAt;
      if (elapsed >= WRAP_TAIL_MS) {
        _fireWrapStop();
      }
    }, 1000);
  }, [_clearWrapTail, _fireWrapStop]);

  const _legLive = useCallback(async (session, opts = {}) => {
    const { origin = 'user', callId = null, agentExtension = null } = opts;
    const legId = _legIdFor(session);

    // Tail handling: a leg arrived while we were in the wrap-up window.
    if (wrapTailTimerRef.current) {
      if (origin === 'continuation') {
        // transfer / conference / merge leg -> keep the SAME recording.
        console.log('[CX-SR] engagement: continuation leg during wrap tail -> keep recording', legId);
        _clearWrapTail();
      } else {
        // brand-new user call -> close the current recording NOW, start fresh.
        console.log('[CX-SR] engagement: NEW user call during wrap tail -> close current, start fresh', legId);
        _clearWrapTail();
        engagementActiveRef.current = false;
        engagementLegsRef.current.clear();
        engagementMetaRef.current = { callId: null, agentExtension: null };
        try {
          if (stopRecordingRef.current) await stopRecordingRef.current();
        } catch (err) {
          console.warn('[CX-SR] engagement: pre-empt stop failed', err);
        }
      }
    }

    engagementLegsRef.current.add(legId);

    if (!engagementActiveRef.current) {
      engagementActiveRef.current = true;
      engagementMetaRef.current = { callId, agentExtension };
      console.log('[CX-SR] engagement: FIRST leg -> startRecording', { legId, origin, callId });
      try {
        if (startRecordingRef.current) {
          await startRecordingRef.current({ callId, agentExtension });
        }
      } catch (err) {
        console.warn('[CX-SR] engagement: startRecording failed', err);
      }
    } else {
      console.log('[CX-SR] engagement: leg added to active engagement', legId,
                  'legs=', engagementLegsRef.current.size);
    }
  }, [_legIdFor, _clearWrapTail]);

  // 2026-08-17 FIX 1: coordinator-owned entry point for SERVER-authoritative
  // triggers (screen-record-start / force-screen-record SSE). Previously the
  // SSE handlers called startRecording() DIRECTLY with a `getState()==='recording'`
  // guard. That guard MISSES the 'preparing' window (the browser's own legLive
  // start is mid /begin round-trip), so the SSE fired a SECOND startRecording()
  // that pre-empted the first -> the two raced and tore each other down -> no
  // clean outbound row (64% outbound gap). Routing the server trigger through
  // the engagement coordinator makes it a DEBOUNCE (rebind the authoritative
  // uniqueid) when an engagement is active OR a start is already in flight,
  // and a clean first-start only when genuinely idle. One recording per
  // interaction, no double-start.
  const _serverStart = useCallback(async ({ uniqueid = null, agentExtension = null } = {}) => {
    const st = stateRef.current;
    // Case 1: an engagement is already active, OR a start is in flight
    // ('preparing'), OR we're recording. Do NOT start a second one -- just
    // rebind to the authoritative Asterisk uniqueid so voice<->screen linking
    // is correct, and (if we were mid-wrap-tail) cancel the tail since the
    // server says the interaction is still live.
    if (engagementActiveRef.current || st === 'preparing' || st === 'recording') {
      if (wrapTailTimerRef.current) {
        console.log('[CX-SR] serverStart: live interaction during wrap tail -> keep recording');
        _clearWrapTail();
      }
      if (uniqueid && recordingIdRef.current) {
        try {
          const out = await screenRecordingsApi.rebindCallId(recordingIdRef.current, uniqueid);
          console.log('[CX-SR] serverStart: debounced (already active/preparing), rebound callId', out);
        } catch (err) {
          console.warn('[CX-SR] serverStart: rebind failed', err);
        }
      } else {
        // No recording id yet (legLive start still resolving /begin). Stash the
        // uniqueid so the fallback rebind poller / outbound-start handler picks
        // it up; do NOT start a competing recording.
        if (uniqueid) {
          window.__cxPendingOutboundCallId = uniqueid;
          window.__cxPendingOutboundCallIdUntil = Date.now() + 10_000;
        }
        console.log('[CX-SR] serverStart: debounced (start in flight), stashed uniqueid');
      }
      return;
    }
    // Case 2: genuinely idle -> start a fresh recording THROUGH the coordinator
    // using a synthetic session, so legGone/wrap-tail bookkeeping stays
    // consistent (a subsequent real legGone or server stop tears it down
    // cleanly). This is the authoritative start for BOTH inbound and outbound.
    const synthetic = { __cxLegId: `leg-srv-${++legSeqRef.current}` };
    console.log('[CX-SR] serverStart: idle -> starting via coordinator', { uniqueid });
    try {
      await _legLive(synthetic, { origin: 'user', callId: uniqueid, agentExtension });
    } catch (err) {
      console.warn('[CX-SR] serverStart: coordinator start failed', err);
    }
  }, [_legLive, _clearWrapTail]);

  // 2026-07-29: reset engagement state when the backend reaper force-closed a
  // stuck recording for this agent. Without this, engagementActiveRef stays
  // true forever and SWALLOWS every subsequent call (no new recording starts).
  // Clears the engagement bookkeeping + best-effort stops any local recorder,
  // so the NEXT legLive() starts a fresh recording. Keeps the acquired screen
  // stream (so no re-prompt).
  const _resetEngagement = useCallback(() => {
    console.warn('[CX-SR] resetEngagement: clearing wedged engagement state');
    try { _clearWrapTail(); } catch (_) {}
    engagementActiveRef.current = false;
    engagementLegsRef.current.clear();
    engagementMetaRef.current = { callId: null, agentExtension: null };
    try {
      if (stopRecordingRef.current) {
        stopRecordingRef.current().catch(() => {});
      }
    } catch (_) { /* swallow */ }
  }, [_clearWrapTail]);

  const _legGone = useCallback((session) => {
    const legId = _legIdFor(session);
    const had = engagementLegsRef.current.delete(legId);
    if (!had) {
      // Unknown leg (never marked live) -> ignore, but log for visibility.
      console.log('[CX-SR] engagement: legGone for untracked leg', legId);
    }
    console.log('[CX-SR] engagement: leg gone', legId, 'remaining=', engagementLegsRef.current.size);
    if (engagementActiveRef.current && engagementLegsRef.current.size === 0) {
      _armWrapTail();
    }
  }, [_legIdFor, _armWrapTail]);

  // Logout: clear all legs and give a wrap tail so the agent's final
  // after-call work is captured, then stop AND release the stream.
  // (Fernando 2026-07-27: short tail on logout too.) If no recording is
  // active, release the stream immediately.
  const _logout = useCallback(() => {
    console.log('[CX-SR] engagement: LOGOUT');
    engagementLegsRef.current.clear();
    if (engagementActiveRef.current) {
      _armWrapTail(true);   // releaseStreamAfter = true
    } else {
      _clearWrapTail();
      try { if (releaseStreamRef.current) releaseStreamRef.current(); } catch (_) {}
    }
  }, [_armWrapTail, _clearWrapTail]);

  // -----------------------------------------------------------------
  // Step 4: releaseStream() \u2014 ends the shift, drops permission
  // -----------------------------------------------------------------

  const releaseStream = useCallback(() => {
    // If a recording is mid-flight, abort it first.
    if (recordingIdRef.current && !stoppedRef.current) {
      stoppedRef.current = true;
      screenRecordingsApi.abort(recordingIdRef.current, {
        failure_reason: 'stream_released_during_recording',
      }).catch(() => { /* swallow */ });
    }
    _resetPerRecording();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => {
        try { t.stop(); } catch (_) { /* swallow */ }
      });
      streamRef.current = null;
    }
    // Multi-monitor (2026-09-10): abort + drop any secondary-screen recorders
    // and release their monitor streams too.
    try {
      (extraRecordersRef.current || []).forEach((rec) => {
        try { rec.stop({}).catch(() => {}); } catch (_) {}
      });
      extraRecordersRef.current = [];
      (extraStreamsRef.current || []).forEach((s) => {
        try { s.getTracks().forEach((t) => t.stop()); } catch (_) {}
      });
      extraStreamsRef.current = [];
    } catch (_) { /* swallow */ }
    setState('idle');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_resetPerRecording]);

  // Publish releaseStream via ref so the engagement wrap tail can drop the
  // screen-share stream after a logout-initiated tail.
  releaseStreamRef.current = releaseStream;

  // -----------------------------------------------------------------
  // Imperative bridge: expose a global handle so useSIP.js can call
  // start/stop without prop-drilling. Set on the window when this hook
  // mounts; cleared on unmount.
  // -----------------------------------------------------------------

  useEffect(() => {
    // rebindCallId (Phase 2.2): swap the current recording's call_id
    // after the backend provides the Asterisk uniqueid via SSE.
    // No-op if no recording is active or if already bound to a uniqueid.
    const rebindCallId = async (newCallId) => {
      const id = recordingIdRef.current;
      if (!id) return { ok: false, reason: 'no_active_recording' };
      try {
        const out = await screenRecordingsApi.rebindCallId(id, newCallId);
        console.log('[CX-SR] rebindCallId result', out);
        return { ok: true, out };
      } catch (err) {
        console.warn('[CX-SR] rebindCallId failed', err);
        return { ok: false, err };
      }
    };
    window.__cxScreenRec = {
      // Legacy direct controls (still used internally + as fallback).
      startRecording,
      stopRecording,
      acquireStream,
      releaseStream,
      rebindCallId,
      getState: () => stateRef.current,
      // 2026-07-27 engagement coordinator API. useSIP.js should call THESE
      // for call lifecycle, not startRecording/stopRecording directly.
      legLive: (session, opts) => _legLive(session, opts),
      // 2026-08-17 FIX 1: server-authoritative start, coordinator-owned
      // (debounces against an active/in-flight start instead of racing it).
      serverStart: (opts) => _serverStart(opts),
      legGone: (session) => _legGone(session),
      logout: () => _logout(),
      resetEngagement: () => _resetEngagement(),
      // Observability for the coverage gate / debugging.
      engagementActive: () => engagementActiveRef.current,
      engagementLegCount: () => engagementLegsRef.current.size,
      // 2026-07-28: connectivity preflight to Azure (used by ScreenShareGate).
      runAzurePreflight: (ext) => runAzurePreflight(ext),
      // Multi-monitor (2026-09-10): opt-in extra screen (gate button).
      acquireExtraScreen: () => acquireExtraScreen(),
      maxScreens: () => Math.max(1, maxScreensRef.current || 1),
      extraScreenCount: () => (extraStreamsRef.current?.length || 0),
    };
    return () => {
      // 2026-07-16: cancel any pending fallback rebind poll on unmount.
      _cancelFallbackPoll();
      // 2026-07-27: clear any pending wrap tail on unmount.
      _clearWrapTail();
      // best-effort abort on unmount
      try {
        if (recordingIdRef.current) {
          screenRecordingsApi.abort(recordingIdRef.current, {
            failure_reason: 'unmount',
          }).catch(() => {});
        }
      } finally {
        _resetPerRecording();
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => {
            try { t.stop(); } catch (_) { /* swallow */ }
          });
          streamRef.current = null;
        }
        delete window.__cxScreenRec;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    error,
    recordingId,
    bytesUploaded,
    chunksUploaded,
    acquireStream,
    acquireExtraScreen,
    startRecording,
    stopRecording,
    releaseStream,
    // Engagement coordinator (2026-07-27).
    legLive: _legLive,
    legGone: _legGone,
    logout: _logout,
    resetEngagement: _resetEngagement,
    serverStart: _serverStart,
    // Legacy alias \u2014 keeps the old `start()` / `stop()` semantics if anyone
    // is calling them: start = acquire (no recording until a call arrives),
    // stop = release everything.
    start: acquireStream,
    stop: releaseStream,
  };
}
